import { useCallback } from "react";
import { gql, useQuery, useApolloClient } from "@apollo/client";

export const PUBLIC_VAPID_KEY =
  process.env.REACT_APP_PUBLIC_VAPID_KEY || "RUNTIME_REPLACE_REACT_APP_PUBLIC_VAPID_KEY";

const entityQuery = gql`
  query pushSubscriptionQuery(
    $pushSubscription_list_request: pushSubscription_list_request
  ) {
    pushSubscription(pushSubscription_list_request: $pushSubscription_list_request) {
      edges {
        id
        user_id
        endpoint
        p256dh
        auth
      }
    }
  }
`;

const pushSubscriptionMutation = gql`
  mutation pushSubscriptionMutate($pushSubscription_mutation_request: MutationInput) {
    pushSubscription(pushSubscription_mutation_request: $pushSubscription_mutation_request) {
      id
      isNew
    }
  }
`;

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function parsePushSubscription(subscription) {
  if (!subscription) return null;
  const raw = typeof subscription.toJSON === "function"
    ? subscription.toJSON()
    : JSON.parse(JSON.stringify(subscription));
  return {
    endpoint: raw.endpoint,
    p256dh: raw.keys?.p256dh,
    auth: raw.keys?.auth,
    raw,
  };
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) {
    await navigator.serviceWorker.ready;
    return existing;
  }
  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return registration;
}

export async function getBrowserPushSubscription(registration) {
  if (!registration?.pushManager) return null;
  return registration.pushManager.getSubscription();
}

/**
 * Create or refresh a browser push subscription.
 * When forceFresh is true, unsubscribes any existing subscription first so a
 * new endpoint is minted (needed after server-side 410 cleanup / iOS flakiness).
 */
export async function subscribeBrowserPush(registration, { forceFresh = false } = {}) {
  if (!registration?.pushManager) {
    throw new Error("Push notifications are not supported in this browser.");
  }

  let existing = await registration.pushManager.getSubscription();
  if (existing && forceFresh) {
    try {
      await existing.unsubscribe();
    } catch (err) {
      console.warn("Failed to unsubscribe existing push subscription:", err);
    }
    existing = null;
  }

  if (existing) {
    return parsePushSubscription(existing);
  }

  const result = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
  });
  return parsePushSubscription(result);
}

export async function findServerPushSubscription(client, { endpoint, userId } = {}) {
  const filters = [];
  if (endpoint) {
    filters.push({ field: "endpoint", operator: "=", value: String(endpoint) });
  }
  if (userId != null && userId !== "") {
    filters.push({ field: "user_id", operator: "=", value: String(userId) });
  }
  if (!filters.length) return [];

  const { data } = await client.query({
    query: entityQuery,
    variables: {
      pushSubscription_list_request: {
        filters,
        sorting: { field: "id", direction: "asc" },
      },
    },
    fetchPolicy: "network-only",
  });

  return data?.pushSubscription?.edges ?? [];
}

// 🔹 Custom Hook to Fetch Subscriptions
export const GetUserSubscriptions = (user_id) => {
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables: {
      pushSubscription_list_request: {
        filters: [
          {
            field: "user_id",
            operator: "=",
            value: String(user_id),
          },
        ],
        sorting: {
          field: "id",
          direction: "asc",
        },
      }
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error(error);
    },
  });

  if (data) {
    return data?.pushSubscription?.edges || [];
  }
  return false;
};

export const useUpdateSubscriptions = () => {
  const client = useApolloClient();

  const addPushSubscription = useCallback(async (endpoint, p256dh, auth, user_id) => {
    if (!endpoint || !p256dh || !auth || user_id == null || user_id === "") {
      return { ok: false, reason: "missing_fields" };
    }

    try {
      localStorage.setItem("subscribing", "true");

      const existingSubscriptions = await findServerPushSubscription(client, { endpoint });
      const existingSubscription = existingSubscriptions[0] ?? null;

      if (
        existingSubscription &&
        String(user_id) === String(existingSubscription.user_id) &&
        p256dh === existingSubscription.p256dh &&
        auth === existingSubscription.auth
      ) {
        localStorage.removeItem("subscribing");
        return { ok: true, skipped: true, id: existingSubscription.id };
      }

      for (const sub of existingSubscriptions) {
        if (String(sub.user_id) !== String(user_id)) {
          try {
            await client.mutate({
              mutation: pushSubscriptionMutation,
              variables: {
                pushSubscription_mutation_request: {
                  id: sub.id,
                  shouldDelete: true,
                  properties: [
                    { key: "endpoint", string: sub.endpoint },
                    { key: "p256dh", string: sub.p256dh },
                    { key: "auth", string: sub.auth },
                    { key: "user_id", string: sub.user_id },
                  ],
                },
              },
            });
            console.log(`Removed subscription for user ${sub.user_id} on device ${endpoint}`);
          } catch (error) {
            console.error(`Error removing subscription for user ${sub.user_id}:`, error);
          }
        }
      }

      const response = await client.mutate({
        mutation: pushSubscriptionMutation,
        variables: {
          pushSubscription_mutation_request: {
            id:
              existingSubscription && String(existingSubscription.user_id) === String(user_id)
                ? existingSubscription.id
                : undefined,
            properties: [
              { key: "endpoint", string: endpoint },
              { key: "p256dh", string: p256dh },
              { key: "auth", string: auth },
              { key: "user_id", string: String(user_id) },
            ],
          },
        },
      });

      console.log(`Subscription assigned to user ${user_id} on device ${endpoint}`);
      localStorage.removeItem("subscribing");
      return {
        ok: true,
        skipped: false,
        id: response?.data?.pushSubscription?.id,
        isNew: response?.data?.pushSubscription?.isNew,
      };
    } catch (error) {
      console.error(error);
      localStorage.removeItem("subscribing");
      return { ok: false, reason: "error", error };
    }
  }, [client]);

  const removePushSubscription = useCallback(async (endpoint, p256dh, auth, user_id) => {
    try {
      const subscriptions = await findServerPushSubscription(client, { endpoint, userId: user_id });
      const subscription = subscriptions[0] ?? null;

      if (!subscription) {
        return { ok: false, reason: "not_found" };
      }
      await client.mutate({
        mutation: pushSubscriptionMutation,
        variables: {
          pushSubscription_mutation_request: {
            id: subscription.id,
            shouldDelete: true,
            properties: [
              { key: "endpoint", string: endpoint },
              { key: "p256dh", string: p256dh },
              { key: "auth", string: auth },
              { key: "user_id", string: String(user_id) },
            ],
          },
        },
      });
      return { ok: true };
    } catch (error) {
      console.error(error);
      return { ok: false, reason: "error", error };
    }
  }, [client]);

  /**
   * Ensure browser subscription exists and is stored for the current user.
   * forceFresh mints a new endpoint (use when notifications stopped working).
   */
  const ensurePushSubscription = useCallback(async (user_id, { forceFresh = false, requestPermission = true } = {}) => {
    if (!isPushSupported()) {
      return { ok: false, reason: "unsupported" };
    }
    if (!user_id) {
      return { ok: false, reason: "no_user" };
    }

    if (Notification.permission === "denied") {
      return { ok: false, reason: "denied" };
    }

    if (Notification.permission !== "granted") {
      if (!requestPermission) {
        return { ok: false, reason: "permission_default" };
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return { ok: false, reason: permission === "denied" ? "denied" : "permission_default" };
      }
    }

    const registration = await getServiceWorkerRegistration();
    const parsed = await subscribeBrowserPush(registration, { forceFresh });
    if (!parsed?.endpoint || !parsed?.p256dh || !parsed?.auth) {
      return { ok: false, reason: "subscribe_failed" };
    }

    const saved = await addPushSubscription(parsed.endpoint, parsed.p256dh, parsed.auth, user_id);
    return {
      ok: saved.ok,
      reason: saved.reason,
      subscription: parsed,
      saved,
    };
  }, [addPushSubscription]);

  const findServerPushSubscriptionForUser = useCallback(
    (args) => findServerPushSubscription(client, args),
    [client]
  );

  return {
    addPushSubscription,
    removePushSubscription,
    ensurePushSubscription,
    findServerPushSubscription: findServerPushSubscriptionForUser,
  };
};
